import { ReactElement, ReactNode } from 'react';
import {
  Link,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import Launch from '@material-ui/icons/Launch';
import AccessTime from '@material-ui/icons/AccessTime';
import { FontFamily } from '@principal-developer-portal/ui';
import { GridRows } from './types/content-props';
import {
  getDueDateString,
  overdueOrIn7Days,
} from '../../data-table/utils/date';
import { DueDate } from '../../data-table/utils/types';

interface Columns {
  field: string;
  headerName: string;
  width: number;
}

export interface ModalContentTableProps {
  columns: Columns[];
  rows: GridRows[];
  isFrameworkMatched: boolean;
}

export const modalContentColumns = [
  {
    field: 'due_date',
    headerName: 'Due date/status',
    width: 224,
    cellClassName: 'data-grid-cell',
  },
  {
    field: 'entity',
    headerName: 'Entity',
    width: 224,
    cellClassName: 'data-grid-cell',
  },
  {
    field: 'owning_team',
    headerName: 'Owning team',
    width: 224,
    cellClassName: 'data-grid-cell',
  },
];

const useStyles = makeStyles(theme => ({
  tableBorder: {
    // @ts-expect-error custom color
    borderBottom: `1px solid ${theme.palette.marketplaceCard?.border}`,
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
  },
  th: {
    textAlign: 'left',
    height: '24px',
    paddingRight: '8px',
    gap: '8px',
    fontSize: '16px',
    lineHeight: '24px',
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  td: {
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingRight: '16px',
    textAlign: 'left',
    maxHeight: '54px',
    backgroundColor: theme.palette.background.paper,
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  focusable: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:focus, &:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconOverdue: {
    color: theme.palette.errorText,
  },
  iconWarning: {
    color: theme.palette.warningText,
  },
}));

// eslint-disable-next-line max-lines-per-function
const CellContent = (
  column: Columns,
  row: GridRows,
  theme: Theme,
  isFrameworkMatched: boolean,
): ReactNode => {
  const classes = useStyles();
  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '14px',
  };

  if (column.field === 'due_date') {
    const dueDateValue: DueDate = {
      text: row[column.field].text,
      date: row[column.field].date ?? null,
    };
    const date = getDueDateString(dueDateValue, isFrameworkMatched);
    const { className, dueSoon } = overdueOrIn7Days(dueDateValue);

    const iconClass =
      // eslint-disable-next-line no-nested-ternary
      className === 'theme--icon-overdue'
        ? classes.iconOverdue
        : className === 'theme--icon-warning'
        ? classes.iconWarning
        : '';

    return (
      <Typography
        style={{
          flex: 1,
          minWidth: '50px',
          maxWidth: '250px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
        data-testid={`table-row-${column.field}`}
      >
        {dueSoon ? (
          <Typography style={{ fontSize: '14px' }}>
            <AccessTime
              className={iconClass}
              style={{ marginBottom: '-8px' }}
              data-testid={`table-row-${column.field}-icon`}
            />{' '}
            <strong>{date}</strong>
          </Typography>
        ) : (
          <Typography
            style={{ fontSize: '14px' }}
            data-testid={`table-row-${column.field}-icon`}
          >
            {date}
          </Typography>
        )}

        {row[column.field].text !== 'N/A' && (
          <Typography
            style={{ ...cellStyle, width: '100%' }}
            title={row[column.field].text}
          >
            {row[column.field].text}
          </Typography>
        )}
      </Typography>
    );
  }

  if (column.field === 'owning_team') {
    return (
      <Typography
        style={{
          ...cellStyle,
          flex: 1,
          minWidth: '50px',
          maxWidth: '250px',
          alignItems: 'center',
          color: `${theme.palette.infoText}`,
        }}
        data-testid={`table-row-${column.field}`}
        title={row[column.field].text}
        role="gridcell"
      >
        <Link
          href={row[column.field]?.link ?? ''}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center' }}
          tabIndex={0}
          className={classes.focusable}
        >
          {row[column.field].text}
        </Link>
      </Typography>
    );
  }

  if (column.field === 'entity') {
    return (
      <Typography
        style={{
          ...cellStyle,
          flex: 1,
          minWidth: '50px',
          maxWidth: '300px',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
        data-testid={`table-row-${column.field}`}
      >
        <Typography
          style={{
            display: 'flex',
            alignItems: 'center',
            ...cellStyle,
            color: `${theme.palette.infoText}`,
          }}
          title={row[column.field].text}
        >
          <Link
            href={row[column.field]?.link ?? ''}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', ...cellStyle }}
            className={classes.focusable}
            tabIndex={0}
          >
            {row[column.field].text}
            <Launch style={{ marginLeft: 4, fontSize: '14px' }} />
          </Link>
        </Typography>
        {row[column.field]?.type === 'resource' ? (
          <Typography style={{ ...cellStyle }} title={row[column.field].text}>
            {row[column.field].resourceType}
          </Typography>
        ) : (
          <Typography style={{ ...cellStyle }} title={row[column.field].text}>
            {row[column.field].nameSpace}
          </Typography>
        )}
      </Typography>
    );
  }

  return (
    <Typography
      style={{
        ...cellStyle,
        flex: 1,
        minWidth: '50px',
        maxWidth: '250px',
        alignItems: 'center',
      }}
      data-testid={`table-row-${column.field}`}
    >
      {row[column.field].text}
    </Typography>
  );
};

export const ModalContentTable = (
  props: ModalContentTableProps,
): ReactElement => {
  const classes = useStyles();
  const theme = useTheme();
  const { columns, rows, isFrameworkMatched } = props;

  return (
    <div>
      <table className={classes.table}>
        <thead>
          <tr className={classes.tableBorder}>
            {columns?.map(column => (
              <th
                key={column.field}
                className={classes.th}
                style={{ width: column.width }}
                data-testid={`table-column-${column.field}`}
              >
                {column.headerName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.map((row, index) => (
            <tr key={index} className={classes.tableBorder}>
              {columns.map(column => (
                <td
                  key={column.field}
                  className={classes.td}
                  style={{
                    width: column.width,
                  }}
                >
                  <Typography
                    className={classes.ellipsis}
                    style={{ display: 'flex', alignItems: 'start' }}
                  >
                    {/* eslint-disable-next-line new-cap */}
                    {CellContent(column, row, theme, isFrameworkMatched)}
                  </Typography>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
